import React, { useState } from "react";
import { api, formatBRL } from "../lib/api";
import { toast } from "sonner";
import { X, Loader2, TrendingDown, AlertCircle, CheckCircle2 } from "lucide-react";

/**
 * iter70 — Modal "Renovação + Amortização de Capital"
 *
 * Props:
 *   - contract: contrato serializado (precisa de id, capital, current_cycle)
 *   - onClose(): fecha modal
 *   - onDone(): callback após sucesso (recarrega a ficha)
 */
export default function RenewWithAmortizationModal({ contract, onClose, onDone }) {
  const [totalReceived, setTotalReceived] = useState("");
  const [newPct, setNewPct] = useState("");
  const [notes, setNotes] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);

  const cc = contract?.current_cycle;
  const interestRemaining = Math.max((cc?.interest_amount || 0) - (cc?.total_paid || 0), 0);
  const capitalAtual = cc?.capital ?? contract?.capital;

  const buildPayload = () => {
    const v = parseFloat(String(totalReceived).replace(",", "."));
    if (!Number.isFinite(v) || v <= 0) return null;
    const payload = { total_received: v };
    if (newPct) {
      const p = parseFloat(String(newPct).replace(",", "."));
      if (Number.isFinite(p)) payload.new_interest_percent = p;
    }
    if (notes) payload.notes = notes;
    return payload;
  };

  const doPreview = async () => {
    const p = buildPayload();
    if (!p) { toast.error("Informe o valor recebido"); return; }
    setPreviewing(true); setPreview(null);
    try {
      const { data } = await api.post(`/contracts/${contract.id}/renew-with-amortization`,
        { ...p, dry_run: true });
      setPreview(data.preview);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Falha ao gerar prévia");
    } finally { setPreviewing(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    const p = buildPayload();
    if (!p) { toast.error("Informe o valor recebido"); return; }
    if (!preview) { toast.error("Gere a prévia antes de confirmar"); return; }
    setSaving(true);
    try {
      await api.post(`/contracts/${contract.id}/renew-with-amortization`,
        { ...p, dry_run: false });
      toast.success(preview.is_quitacao
        ? "Contrato QUITADO com amortização total"
        : `Renovação + amortização (novo capital: ${formatBRL(preview.novo_capital)})`);
      onDone && onDone();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Falha ao confirmar operação");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 bg-black/70 backdrop-blur-sm overflow-y-auto" data-testid="renew-amort-modal">
      <form onSubmit={submit} className="w-full max-w-2xl rounded-lg border border-[#262626] bg-[#0d0d0d] shadow-2xl my-8">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#1f1f1f]">
          <h3 className="font-display text-base flex items-center gap-2 text-[#D4AF37]">
            <TrendingDown size={16} /> Renovação + amortização de capital
          </h3>
          <button type="button" onClick={onClose} className="text-[#737373] hover:text-white" data-testid="renew-amort-close">
            <X size={18} />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <div className="rounded-md border border-[#D4AF37]/25 bg-[#0f0f0f] p-3 text-[12px] text-[#A3A3A3]">
            Informe o valor total recebido do cliente. O sistema separa automaticamente:
            <span className="text-[#D4AF37]"> juros do ciclo</span> (creditados pela regra oficial ao sócio/empresa) +
            <span className="text-emerald-300"> amortização de capital</span> (reduz o capital em aberto e retorna à tesouraria).
          </div>

          <div className="grid grid-cols-2 gap-3 text-[12px]">
            <SumCell label="Capital atual" value={formatBRL(capitalAtual)} accent="gold" />
            <SumCell label="Juros restantes do ciclo" value={formatBRL(interestRemaining)} accent="amber" />
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-wider text-[#737373] block mb-1">
              Valor total recebido (R$)
            </label>
            <input
              type="number" step="0.01" min="0" required
              placeholder={`Mínimo: ${formatBRL(interestRemaining)}`}
              className="fc-input w-full text-[13px]"
              value={totalReceived}
              onChange={(e) => { setTotalReceived(e.target.value); setPreview(null); }}
              data-testid="renew-amort-total"
            />
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-wider text-[#737373] block mb-1">
              Novo % de juros <span className="text-[#737373] font-normal">(opcional — mantém atual)</span>
            </label>
            <input
              type="number" step="0.01" min="0"
              placeholder={`atual: ${contract?.interest_percent ?? 0}%`}
              className="fc-input w-full text-[13px]"
              value={newPct}
              onChange={(e) => { setNewPct(e.target.value); setPreview(null); }}
              data-testid="renew-amort-newpct"
            />
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-wider text-[#737373] block mb-1">
              Observação
            </label>
            <input
              type="text"
              placeholder="Ex.: cliente amortizou R$ 500 do capital"
              className="fc-input w-full text-[13px]"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid="renew-amort-notes"
            />
          </div>

          <button type="button" onClick={doPreview} disabled={previewing || !totalReceived}
                  className="fc-btn-ghost text-xs w-full flex items-center justify-center gap-1.5"
                  data-testid="renew-amort-preview-btn">
            {previewing ? <Loader2 size={12} className="animate-spin" /> : null} Gerar prévia
          </button>

          {preview && (
            <div className="rounded-md border border-[#D4AF37]/30 bg-[#0f0f0f] p-4 space-y-3" data-testid="renew-amort-preview">
              <div className="text-[11px] uppercase tracking-wider text-[#D4AF37] font-semibold flex items-center gap-1.5">
                {preview.is_quitacao ? <CheckCircle2 size={12} className="text-emerald-300"/> : <AlertCircle size={12}/>}
                {preview.is_quitacao ? "Prévia — QUITAÇÃO TOTAL" : "Prévia — nova operação"}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-[11.5px]">
                <SumCell label="Capital atual" value={formatBRL(preview.capital_atual)} />
                <SumCell label="Juros ciclo" value={formatBRL(preview.juros_ciclo)} />
                <SumCell label="Valor recebido" value={formatBRL(preview.valor_recebido)} accent="gold" />
                <SumCell label="Amortização" value={formatBRL(preview.amortizacao)} accent="emerald" />
                <SumCell label="Novo capital" value={formatBRL(preview.novo_capital)} accent="gold" />
                <SumCell label="Próximos juros" value={formatBRL(preview.proximo_juros)} accent={preview.is_quitacao ? "neutral" : "amber"} />
              </div>
              <div className="grid grid-cols-2 gap-2 text-[11.5px] border-t border-[#1f1f1f] pt-2">
                <SumCell label="Sócio nos juros" value={formatBRL(preview.socio_share_juros)} />
                <SumCell label="Empresa nos juros" value={formatBRL(preview.empresa_share_juros)} />
                {preview.compensacao_antecipacao > 0 && (
                  <SumCell label="Antecipação compensada" value={formatBRL(preview.compensacao_antecipacao)} accent="amber" />
                )}
                <SumCell
                  label="Capital devolvido ao sócio"
                  value={formatBRL(preview.capital_devolvido_socio)}
                  accent="emerald"
                />
              </div>
              {preview.in_recovery && (
                <div className="text-[11px] text-amber-300 border border-amber-500/30 bg-amber-500/5 rounded p-2">
                  ⚠️ Contrato em Recuperação — permanece em Recuperação após amortização.
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 px-5 py-3 border-t border-[#1f1f1f] bg-[#0a0a0a]/50">
          <button type="button" onClick={onClose} className="fc-btn-ghost text-xs">Cancelar</button>
          <button
            type="submit"
            disabled={saving || !preview}
            className="fc-btn-gold text-xs flex items-center gap-1.5"
            data-testid="renew-amort-submit"
          >
            {saving && <Loader2 size={12} className="animate-spin" />}
            Confirmar renovação + amortização
          </button>
        </div>
      </form>
    </div>
  );
}

function SumCell({ label, value, accent = "neutral" }) {
  const colors = {
    gold: "text-[#D4AF37]",
    emerald: "text-emerald-300",
    amber: "text-amber-300",
    neutral: "text-[#A3A3A3]",
  };
  return (
    <div className="rounded border border-[#1f1f1f] bg-[#0a0a0a] px-2 py-1.5">
      <div className="text-[9.5px] uppercase tracking-wider text-[#737373]">{label}</div>
      <div className={`font-mono-tab text-[12.5px] mt-0.5 ${colors[accent]}`}>{value}</div>
    </div>
  );
}
